// Planque en 3D : un hangar vu de haut, une pièce par module, en low-poly. Le décor de chaque pièce suit le niveau
// du module (pas construit : bâche et caisses). Étiquettes HTML posées sur la vue, clic sur une pièce pour la choisir.
import * as THREE from 'three';
import { flat } from './lowpoly';
import { buildHelmet, buildVest } from './gear';
import { MODULES, type ModuleId } from '../shared/hideout';

const svg = (d: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
export const MODULE_ICON: Record<ModuleId, string> = {
  generator: svg('<path d="M13 2.5 5 13.5h6l-1 8 8-11h-6z"/>'),
  workbench: svg('<circle cx="12" cy="12" r="3"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/>'),
  medstation: svg('<rect x="3.5" y="3.5" width="17" height="17" rx="8.5"/><path d="M10 7.5h4v4.5h4.5v4H14v-.5M10 7.5v4.5H5.5v4H10v-.5"/>'),
  storage: svg('<path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z"/><path d="M3.5 7.5 12 11.5l8.5-4M12 11.5v9"/>'),
  tailor: svg('<circle cx="6.5" cy="6.5" r="2.6"/><circle cx="6.5" cy="17.5" r="2.6"/><path d="M8.6 8.1 20 17M8.6 15.9 20 7"/>'),
  radio: svg('<path d="M12 21v-9M8 21h8M8.5 9.5a5 5 0 0 1 7 0M6 7a8.5 8.5 0 0 1 12 0"/><circle cx="12" cy="12" r="1.3"/>'),
  armory: svg('<path d="M4 20 15.5 8.5M20 20 8.5 8.5M14 4l6 6-2 2-6-6zM10 4 4 10l2 2 6-6z"/>'),
  rest: svg('<path d="M3 7v13M3 14h18v6M21 14v-2.5a3 3 0 0 0-3-3h-7v5.5M6.5 11.5h.01"/>'),
};

interface Room { id: ModuleId; x: number; z: number; w: number; d: number; anchor: [number, number, number]; tag: [number, number] }
// Pièces du hangar (mètres) : centre, largeur, profondeur, point visé par l'étiquette et décalage de l'étiquette (pixels)
const ROOMS: Room[] = [
  { id: 'generator', x: -10.5, z: -5.2, w: 6.6, d: 5.6, anchor: [-0.6, 1.4, 0.1], tag: [-10, -110] },
  { id: 'workbench', x: -3.5, z: -5.2, w: 6.6, d: 5.6, anchor: [0, 1.3, -1.6], tag: [0, -130] },
  { id: 'medstation', x: 3.5, z: -5.2, w: 6.6, d: 5.6, anchor: [1.4, 0.9, 0.4], tag: [-50, -120] },
  { id: 'radio', x: 10.5, z: -5.2, w: 6.6, d: 5.6, anchor: [-0.9, 1.1, -2.2], tag: [40, -95] },
  { id: 'storage', x: -10.5, z: 3.4, w: 6.6, d: 5.8, anchor: [0, 1.6, -2.3], tag: [0, -80] },
  { id: 'armory', x: -3.5, z: 3.4, w: 6.6, d: 5.8, anchor: [-0.8, 1.0, 0.4], tag: [0, -90] },
  { id: 'tailor', x: 3.5, z: 3.4, w: 6.6, d: 5.8, anchor: [1.3, 1.5, 0.5], tag: [-60, -85] },
  { id: 'rest', x: 10.5, z: 3.4, w: 6.6, d: 5.8, anchor: [-1.5, 0.8, -0.7], tag: [70, -60] },
];

const C = {
  floor: 0x3a3c3e, floorMat: 0x444648, wall: 0x4a4d50, wallDark: 0x383b3e, trim: 0x23262a, metal: 0x5d6266, metalDark: 0x3f4448,
  wood: 0x7a5a3a, woodDark: 0x5a412b, olive: 0x4f5636, oliveDark: 0x3c4229, tan: 0x8d7a58, yellow: 0xd9a52a, red: 0xb33a2a,
  cloth: 0xd8d4c8, white: 0xe8e4da, tarp: 0x4a5040,
};

// Boîte posée par sa base : (x, y, z) = centre au sol
function B(g: THREE.Object3D, w: number, h: number, d: number, color: number | THREE.Material, x: number, y: number, z: number, ry = 0) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), typeof color === 'number' ? flat(color) : color);
  m.position.set(x, y + h / 2, z);
  m.rotation.y = ry;
  m.castShadow = true;
  m.receiveShadow = true;
  g.add(m);
  return m;
}
function Cyl(g: THREE.Object3D, r: number, h: number, color: number | THREE.Material, x: number, y: number, z: number, rx = 0, rz = 0, seg = 8) {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, seg), typeof color === 'number' ? flat(color) : color);
  m.position.set(x, y, z);
  m.rotation.set(rx, 0, rz);
  m.castShadow = true;
  g.add(m);
  return m;
}
const glow = (c: number) => flat(c, { emissive: c });

// Hasard reproductible pour le décor
let seed = 7;
const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

// Étagère métallique remplie de caisses
function shelf(g: THREE.Object3D, x: number, z: number, ry = 0, w = 1.8, h = 2, fill = 0.8) {
  const s = new THREE.Group();
  s.position.set(x, 0, z);
  s.rotation.y = ry;
  for (const px of [-w / 2, w / 2]) for (const pz of [-0.28, 0.28]) B(s, 0.05, h, 0.05, C.metalDark, px, 0, pz);
  const crates = [C.olive, C.tan, C.oliveDark, 0x6b5a40, 0x55603a];
  for (let i = 0; i < 4; i++) {
    const y = 0.1 + i * (h - 0.15) / 3.2;
    B(s, w, 0.04, 0.6, C.metal, 0, y, 0);
    if (i === 3) break;
    let cx = -w / 2 + 0.08;
    while (cx < w / 2 - 0.3) {
      const cw = 0.28 + rnd() * 0.32;
      if (rnd() < fill && cx + cw < w / 2) B(s, cw - 0.04, 0.22 + rnd() * 0.25, 0.42 + rnd() * 0.1, crates[Math.floor(rnd() * crates.length)]!, cx + cw / 2, y + 0.04, 0);
      cx += cw;
    }
  }
  g.add(s);
  return s;
}
function crate(g: THREE.Object3D, x: number, z: number, w = 0.9, h = 0.55, d = 0.6, color = C.olive, ry = 0, y = 0) {
  B(g, w, h, d, color, x, y, z, ry);
  B(g, w + 0.03, 0.06, d + 0.03, new THREE.Color(color).multiplyScalar(0.75).getHex(), x, y + h, z, ry);
}
function locker(g: THREE.Object3D, x: number, z: number, ry = 0, color = 0x4b5446) {
  const l = new THREE.Group();
  l.position.set(x, 0, z); l.rotation.y = ry;
  B(l, 0.55, 1.9, 0.5, color, 0, 0, 0);
  for (const y of [1.45, 1.55, 1.65]) B(l, 0.3, 0.02, 0.02, C.trim, 0, y, 0.26);
  B(l, 0.03, 0.15, 0.03, C.metal, 0.2, 1.0, 0.26);
  g.add(l);
}
function hangLamp(g: THREE.Object3D, x: number, z: number, y = 3.6, w = 1.3) {
  B(g, w, 0.07, 0.22, C.metalDark, x, y, z);
  B(g, w - 0.1, 0.03, 0.14, glow(0xfff1d6), x, y - 0.03, z);
  for (const s of [-1, 1]) B(g, 0.015, 1.2, 0.015, C.trim, x + s * (w / 2 - 0.1), y + 0.07, z);
}
function wallLamp(g: THREE.Object3D, x: number, y: number, z: number) {
  B(g, 0.45, 0.1, 0.18, C.metalDark, x, y, z);
  B(g, 0.38, 0.03, 0.12, glow(0xffe7c0), x, y - 0.02, z + 0.02);
}
// Pièce pas construite : bâche sur une forme, caisses, chevalets rayés
function placeholder(g: THREE.Object3D, w: number, d: number) {
  const tarp = B(g, 1.8, 0.9, 1.1, C.tarp, -0.3, 0, -0.6, 0.15);
  tarp.scale.set(1, 1, 1);
  B(g, 1.6, 0.4, 1.0, C.tarp, -0.3, 0.9, -0.6, 0.1);
  crate(g, 1.3, -0.9, 0.8, 0.5, 0.6, C.oliveDark, 0.3);
  crate(g, 1.2, 0.0, 0.6, 0.4, 0.5, C.tan, -0.2);
  for (const [x, z] of [[-w / 2 + 0.8, d / 2 - 0.5], [w / 2 - 1.4, d / 2 - 0.5]] as const) {
    for (const s of [-0.4, 0.4]) B(g, 0.05, 0.75, 0.05, C.trim, x + s, 0, z);
    for (let i = 0; i < 4; i++) B(g, 0.2, 0.16, 0.06, i % 2 ? C.yellow : 0x1b1b1b, x - 0.3 + i * 0.2, 0.6, z);
  }
}

// ---------------------------------------------------------------- Décor des modules

function generator(g: THREE.Group, lvl: number, w: number, d: number) {
  B(g, 1.3, 1.7, 0.45, 0x6a6f72, 1.6, 0, -d / 2 + 0.35); // armoire électrique
  B(g, 1.2, 0.05, 0.02, C.trim, 1.6, 1.2, -d / 2 + 0.58);
  for (let i = 0; i < 3; i++) B(g, 0.08, 0.08, 0.04, glow(lvl ? [0x5fe08a, 0xe8a33d, 0x5fe08a][i]! : 0xb03a2e), 1.25 + i * 0.18, 1.45, -d / 2 + 0.58);
  B(g, 0.08, 1.8, 0.08, C.trim, 2.15, 1.7, -d / 2 + 0.3); // gaine de câbles vers le plafond
  if (!lvl) { placeholder(g, w, d); return; }
  B(g, 2.5, 0.14, 1.5, C.woodDark, -0.6, 0, 0.1); // palette
  B(g, 2.2, 1.15, 1.25, C.yellow, -0.6, 0.14, 0.1);
  B(g, 2.24, 0.12, 1.29, 0xb3861f, -0.6, 1.29, 0.1);
  for (let i = 0; i < 5; i++) B(g, 0.06, 0.6, 0.02, 0x2a2a28, -1.4 + i * 0.16, 0.45, 0.74);
  B(g, 0.6, 0.4, 0.02, 0x2a2a28, 0.05, 0.6, 0.74); // tableau de bord
  for (let i = 0; i < 3; i++) B(g, 0.07, 0.07, 0.02, glow(i === 1 ? 0xe8a33d : 0x5fe08a), -0.1 + i * 0.15, 0.85, 0.76);
  B(g, 0.32, 0.28, 0.02, 0x1b1b1b, -0.6, 0.85, 0.74); // pictogramme danger
  B(g, 0.22, 0.2, 0.02, C.yellow, -0.6, 0.89, 0.751);
  Cyl(g, 0.07, 0.9, C.metalDark, -1.4, 1.75, -0.2);
  for (let i = 0; i < 3; i++) { B(g, 0.32, 0.46, 0.18, C.red, 1.1 + i * 0.38, 0, 0.9, 0.1 * i); B(g, 0.06, 0.08, 0.06, C.trim, 1.18 + i * 0.38, 0.46, 0.9); }
  B(g, 2.6, 0.04, 0.06, C.trim, 0.6, 0, 0.75, 0.2); // câble au sol
  if (lvl >= 2) {
    B(g, 1.1, 0.8, 0.8, 0x6b7a44, -2.3, 0, -1.5); // batteries
    for (let i = 0; i < 4; i++) B(g, 0.2, 0.12, 0.6, 0x2a2c28, -2.7 + i * 0.26, 0.8, -1.5);
    B(g, 0.08, 0.08, 0.04, glow(0x5fe08a), -1.8, 0.6, -1.08);
  }
}

function workbench(g: THREE.Group, lvl: number, w: number, d: number) {
  const z0 = -d / 2 + 0.55;
  if (!lvl) { placeholder(g, w, d); return; }
  B(g, 3.0, 1.4, 0.06, 0x6e5236, 0, 1.1, -d / 2 + 0.08); // panneau perforé
  const tools = 4 + lvl * 3;
  for (let i = 0; i < tools; i++) {
    const len = 0.22 + ((i * 37) % 7) * 0.05;
    B(g, 0.05, len, 0.03, i % 3 ? 0xa7aaac : 0xc9a24a, -1.3 + i * (2.6 / tools), 2.25 - len, -d / 2 + 0.13);
  }
  B(g, 3.0, 0.08, 0.95, C.wood, 0, 0.88, z0); // plateau
  for (const x of [-1.4, 1.4]) for (const z of [-0.4, 0.4]) B(g, 0.07, 0.88, 0.07, C.metalDark, x, 0, z0 + z);
  B(g, 2.9, 0.04, 0.85, C.metalDark, 0, 0.25, z0);
  B(g, 0.65, 0.84, 0.85, 0x4a5560, 1.1, 0, z0); // caisson à tiroirs
  for (let i = 0; i < 4; i++) B(g, 0.55, 0.02, 0.02, C.trim, 1.1, 0.18 + i * 0.19, z0 + 0.43);
  B(g, 0.55, 0.28, 0.26, C.red, -0.7, 0.96, z0 - 0.1); // caisse à outils
  B(g, 0.3, 0.05, 0.05, C.trim, -0.7, 1.24, z0 - 0.1);
  B(g, 0.18, 0.2, 0.15, C.metal, 0.3, 0.96, z0 + 0.25); // étau
  for (let i = 0; i < 4; i++) B(g, 0.09, 0.14, 0.09, [0xc9a24a, 0x8a9a5a, 0xc9a24a, 0x6b7a44][i]!, -0.15 + i * 0.12, 0.96, z0 + 0.2); // douilles
  B(g, 0.9, 0.05, 0.2, C.metalDark, 0, 1.95, z0 - 0.25); // lampe d'atelier
  B(g, 0.8, 0.02, 0.14, glow(0xfff1d6), 0, 1.93, z0 - 0.25);
  Cyl(g, 0.24, 0.05, 0x9a5a2a, -0.2, 0.62, z0 + 1.05, 0, 0, 10); // tabouret
  for (let i = 0; i < 3; i++) B(g, 0.04, 0.6, 0.04, C.metalDark, -0.2 + Math.cos(i * 2.1) * 0.15, 0, z0 + 1.05 + Math.sin(i * 2.1) * 0.15);
  crate(g, -2.6, z0 + 0.1, 0.8, 0.55, 0.6, C.olive);
  crate(g, -2.6, z0 + 0.1, 0.7, 0.4, 0.5, C.tan, 0.2, 0.61);
  crate(g, -2.4, z0 + 1.0, 0.6, 0.45, 0.5, C.oliveDark, -0.3);
  B(g, 0.45, 0.35, 0.3, 0x2d4a5e, 2.4, 0, z0 + 0.9); // jerrican d'huile
  if (lvl >= 2) shelf(g, 2.7, -d / 2 + 0.5, 0, 1.0, 1.9, 0.9);
  if (lvl >= 3) { B(g, 0.5, 0.3, 0.3, C.red, 2.2, 0.9 + 0.04, z0 + 0.1); crate(g, 2.3, z0 + 1.8, 0.7, 0.35, 0.45, 0x6b7a44, 0.4); }
}

function medstation(g: THREE.Group, lvl: number, w: number, d: number) {
  // Drapeau à croix rouge
  B(g, 1.3, 0.95, 0.03, C.white, 1.8, 1.4, -d / 2 + 0.06);
  B(g, 0.6, 0.18, 0.02, 0xc43b30, 1.8, 1.79, -d / 2 + 0.085);
  B(g, 0.18, 0.6, 0.02, 0xc43b30, 1.8, 1.58, -d / 2 + 0.085);
  if (!lvl) { placeholder(g, w, d); return; }
  // Lit : cadre, matelas, oreiller
  const bz = 0.4;
  B(g, 2.0, 0.08, 0.9, C.metal, 1.4, 0.45, bz);
  for (const x of [0.45, 2.35]) for (const z of [-0.4, 0.4]) B(g, 0.05, 0.45, 0.05, C.metalDark, x, 0, bz + z);
  B(g, 1.9, 0.16, 0.82, C.cloth, 1.4, 0.53, bz);
  B(g, 0.45, 0.1, 0.6, C.white, 2.05, 0.69, bz);
  B(g, 0.05, 0.5, 0.9, C.metalDark, 2.4, 0.45, bz);
  B(g, 0.9, 0.04, 0.84, 0x8fa0a8, 0.95, 0.7, bz); // drap replié
  // Étagère de soins
  const sh = new THREE.Group(); sh.position.set(-1.6, 0, -d / 2 + 0.45); g.add(sh);
  for (const px of [-0.6, 0.6]) for (const pz of [-0.22, 0.22]) B(sh, 0.04, 1.9, 0.04, C.metalDark, px, 0, pz);
  for (let i = 0; i < 4; i++) {
    B(sh, 1.2, 0.03, 0.48, C.metal, 0, 0.15 + i * 0.55, 0);
    if (i < 3) for (let k = 0; k < 4; k++) B(sh, 0.18, 0.14 + (k % 2) * 0.08, 0.2, [C.white, 0xc43b30, 0x8fb0c8, C.white][(k + i) % 4]!, -0.42 + k * 0.28, 0.18 + i * 0.55, 0);
  }
  // Trousse de secours sur un caisson
  B(g, 0.7, 0.75, 0.5, 0x6e7378, 0.2, 0, -d / 2 + 0.4);
  B(g, 0.45, 0.28, 0.28, 0xc43b30, 0.2, 0.75, -d / 2 + 0.4);
  B(g, 0.2, 0.06, 0.02, C.white, 0.2, 0.86, -d / 2 + 0.55);
  B(g, 0.06, 0.2, 0.02, C.white, 0.2, 0.79, -d / 2 + 0.55);
  // Rideaux d'hôpital
  for (let i = 0; i < 5; i++) B(g, 0.42, 2.5, 0.05, i % 2 ? 0x55604c : 0x4b5644, -w / 2 + 0.5, 0, -1.6 + i * 0.4, 0.5 * (i % 2 ? 1 : -1));
  B(g, 0.04, 0.04, 2.4, C.metalDark, -w / 2 + 0.5, 2.55, -0.8);
  if (lvl >= 2) {
    Cyl(g, 0.02, 1.8, C.metal, 2.75, 0.9, 1.1); // perfusion
    B(g, 0.14, 0.24, 0.06, 0xc8dce6, 2.75, 1.55, 1.1);
    B(g, 0.5, 0.04, 0.5, C.metalDark, 2.75, 0, 1.1);
    B(g, 0.8, 0.9, 0.5, C.white, -0.4, 0, 1.6); // armoire à pharmacie
    B(g, 0.06, 0.25, 0.02, 0xc43b30, -0.4, 0.55, 1.86);
  }
}

function storage(g: THREE.Group, lvl: number, w: number, d: number) {
  // Grillage sur le devant et le côté
  const fence = flat(0x8a9095, { opacity: 0.32 });
  for (const [x, z, len, ry] of [[0.6, d / 2 - 0.05, w - 1.4, 0], [w / 2 - 0.05, 0.2, d - 0.4, Math.PI / 2]] as const) {
    const f = new THREE.Mesh(new THREE.PlaneGeometry(len, 2.2), fence);
    f.position.set(x, 1.1, z); f.rotation.y = ry; g.add(f);
    for (let i = 0; i <= Math.round(len / 1.5); i++) {
      const t = -len / 2 + i * (len / Math.round(len / 1.5));
      B(g, 0.06, 2.3, 0.06, C.metalDark, ry ? x : x + t, 0, ry ? z + t : z);
    }
    B(g, ry ? 0.05 : len, 0.05, ry ? len : 0.05, C.metalDark, x, 2.25, z);
  }
  const n = Math.min(4, 1 + lvl);
  const spots: [number, number, number][] = [[-1.2, -d / 2 + 0.5, 0], [0.8, -d / 2 + 0.5, 0], [-w / 2 + 0.45, 0.3, Math.PI / 2], [0.8, -0.6, 0]];
  for (let i = 0; i < n; i++) shelf(g, spots[i]![0], spots[i]![1], spots[i]![2]);
  locker(g, 2.3, -d / 2 + 0.4); locker(g, 1.7 + 0.6 + 0.0, -d / 2 + 1.0, -Math.PI / 2);
  crate(g, -0.4, 1.4, 1.0, 0.6, 0.7, C.olive, 0.15);
  crate(g, -0.4, 1.4, 0.8, 0.45, 0.6, C.tan, -0.1, 0.66);
  crate(g, 0.9, 1.6, 0.8, 0.55, 0.6, C.oliveDark, 0.3);
  crate(g, -1.4, 2.2, 0.7, 0.4, 0.5, 0x6b5a40, -0.2);
  if (!lvl) { B(g, 1.2, 0.5, 0.9, C.tarp, 1.0, 0, 0.4, 0.2); }
}

function tailor(g: THREE.Group, lvl: number, w: number, d: number) {
  // Étagère de tissus au fond, casiers
  const sh = new THREE.Group(); sh.position.set(-1.6, 0, -d / 2 + 0.4); g.add(sh);
  for (const px of [-0.8, 0.8]) for (const pz of [-0.22, 0.22]) B(sh, 0.04, 1.9, 0.04, C.metalDark, px, 0, pz);
  for (let i = 0; i < 4; i++) {
    B(sh, 1.6, 0.03, 0.48, C.metal, 0, 0.15 + i * 0.55, 0);
    if (i < 3) for (let k = 0; k < 4; k++) B(sh, 0.34, 0.16 + (k % 2) * 0.06, 0.4, [0x4f5636, 0x2f3d4c, 0x8d7a58, 0x5a4a3a][(k + i) % 4]!, -0.57 + k * 0.38, 0.18 + i * 0.55, 0);
  }
  locker(g, 1.4, -d / 2 + 0.35); locker(g, 2.0, -d / 2 + 0.35);
  if (!lvl) { placeholder(g, w, d); return; }
  // Table de couture : rouleaux de tissu, machine
  const tz = 0.7;
  B(g, 2.0, 0.07, 0.8, C.wood, -1.3, 0.82, tz);
  for (const x of [-2.2, -0.4]) for (const z of [-0.35, 0.35]) B(g, 0.06, 0.82, 0.06, C.metalDark, x, 0, tz + z);
  for (let i = 0; i < 3; i++) Cyl(g, 0.09, 0.7, [0x4f5636, 0x2f3d4c, 0x8d7a58][i]!, -1.95 + i * 0.22, 1.0, tz - 0.1, 0, Math.PI / 2);
  B(g, 0.45, 0.3, 0.22, C.white, -0.8, 0.89, tz); // machine à coudre
  B(g, 0.12, 0.2, 0.2, C.white, -0.97, 1.19, tz);
  B(g, 0.45, 0.08, 0.18, C.white, -0.8, 1.39, tz);
  B(g, 0.05, 0.12, 0.05, C.metal, -0.62, 1.25, tz);
  // Mannequin avec gilet (et casque au niveau 2)
  const man = new THREE.Group(); man.position.set(1.3, 0, 0.5); man.rotation.y = -0.4; g.add(man);
  B(man, 0.45, 0.05, 0.45, C.metalDark, 0, 0, 0);
  B(man, 0.05, 0.9, 0.05, C.metal, 0, 0.05, 0);
  B(man, 0.4, 0.62, 0.26, 0xc2ad8a, 0, 0.95, 0);
  B(man, 0.09, 0.1, 0.09, 0xc2ad8a, 0, 1.57, 0);
  B(man, 0.2, 0.24, 0.22, 0xc2ad8a, 0, 1.66, 0);
  const vest = buildVest(lvl >= 2 ? 'v_mil' : 'v_police', 0.6);
  vest.position.set(0, 0.92, 0); man.add(vest);
  if (lvl >= 2) { const h = buildHelmet('h_mil'); h.position.set(0, 1.76, 0); h.scale.setScalar(0.75); man.add(h); }
  crate(g, 2.4, 1.6, 0.7, 0.4, 0.5, 0x6b7a44, -0.2);
}

function armory(g: THREE.Group, lvl: number, w: number, d: number) {
  // Mur d'armes : panneau perforé, fusils accrochés (plus nombreux avec le niveau)
  B(g, 3.2, 1.4, 0.06, 0x6e5236, -0.9, 0.9, -d / 2 + 0.1);
  const guns = lvl ? 3 + lvl * 2 : 2;
  for (let i = 0; i < guns; i++) {
    const x = -2.3 + i * (2.9 / Math.max(guns - 1, 1)), long = i % 3 !== 2;
    B(g, 0.07, long ? 1.0 : 0.55, 0.05, 0x26282a, x, long ? 1.05 : 1.4, -d / 2 + 0.16);
    B(g, 0.11, 0.3, 0.05, i % 2 ? 0x5a412b : 0x3a2e24, x, long ? 1.0 : 1.35, -d / 2 + 0.17);
    B(g, 0.05, 0.14, 0.05, 0x26282a, x + 0.06, long ? 1.45 : 1.6, -d / 2 + 0.17);
  }
  locker(g, 1.6, -d / 2 + 0.35, 0, 0x3e4530); locker(g, 2.2, -d / 2 + 0.35, 0, 0x3e4530);
  if (!lvl) { placeholder(g, w, d); return; }
  // Table de montage : fusil démonté, chargeurs, lampe
  const tz = 0.4;
  B(g, 2.4, 0.08, 0.9, 0x4a4f54, -0.6, 0.86, tz);
  for (const x of [-1.7, 0.5]) for (const z of [-0.38, 0.38]) B(g, 0.07, 0.86, 0.07, C.metalDark, x, 0, tz + z);
  B(g, 1.1, 0.07, 0.12, 0x26282a, -0.9, 0.94, tz - 0.1, 0.1); // carcasse
  B(g, 0.35, 0.09, 0.1, 0x3a2e24, -0.1, 0.94, tz - 0.05, 0.1); // crosse posée à côté
  for (let i = 0; i < 3; i++) B(g, 0.08, 0.2, 0.05, 0x2a2c28, -1.5 + i * 0.13, 0.94, tz + 0.25, 0.2); // chargeurs
  B(g, 0.6, 0.04, 0.16, C.metalDark, -0.6, 1.75, tz - 0.2);
  B(g, 0.5, 0.02, 0.1, glow(0xfff1d6), -0.6, 1.73, tz - 0.2);
  // Caisses de munitions
  crate(g, 1.7, 0.8, 0.8, 0.45, 0.5, 0x55603a, 0.3);
  crate(g, 1.8, 0.8, 0.6, 0.35, 0.45, 0x6b7a44, -0.2, 0.51);
  crate(g, 2.3, 1.8, 0.7, 0.4, 0.5, 0x4f5636, 0.6);
  if (lvl >= 2) {
    const h = buildHelmet('h_mil'); h.position.set(0.25, 1.1, tz + 0.1); h.scale.setScalar(0.9); g.add(h);
    B(g, 0.5, 1.4, 0.5, 0x2e3236, -2.6, 0, 1.5); // coffre-fort à munitions
    for (let i = 0; i < 3; i++) B(g, 0.4, 0.08, 0.05, glow(0x5fe08a), -2.6, 0.4 + i * 0.35, 1.76);
  }
}

function radio(g: THREE.Group, lvl: number, w: number, d: number) {
  // Carte de la zone au mur, table et chaise
  B(g, 1.6, 1.0, 0.03, 0xc9b98f, 1.2, 1.3, -d / 2 + 0.1);
  for (const [x, y] of [[0.8, 1.9], [1.5, 1.6], [1.1, 1.5]] as const) B(g, 0.06, 0.06, 0.02, 0xc43b30, x, y, -d / 2 + 0.12);
  B(g, 1.8, 0.07, 0.8, C.woodDark, -0.6, 0.76, -d / 2 + 0.7);
  for (const x of [-1.4, 0.2]) for (const z of [0.35, 1.05]) B(g, 0.06, 0.76, 0.06, C.metalDark, x, 0, -d / 2 + z);
  B(g, 0.45, 0.06, 0.45, 0x3f4448, -0.6, 0.45, -d / 2 + 1.5); // chaise
  B(g, 0.45, 0.5, 0.06, 0x3f4448, -0.6, 0.45, -d / 2 + 1.72);
  for (const x of [-0.8, -0.4]) for (const z of [1.3, 1.7]) B(g, 0.04, 0.45, 0.04, C.metalDark, x, 0, -d / 2 + z);
  // Poste radio : éteint tant que le module n'est pas construit
  B(g, 0.6, 0.34, 0.32, 0x55603a, -0.9, 0.83, -d / 2 + 0.6);
  B(g, 0.22, 0.12, 0.02, glow(lvl ? 0xe8a33d : 0x3a3a30), -0.95, 0.99, -d / 2 + 0.77);
  Cyl(g, 0.012, 0.8, C.metal, -0.68, 1.55, -d / 2 + 0.5);
  B(g, 0.22, 0.06, 0.16, 0x1b1b1b, -0.2, 0.83, -d / 2 + 0.7); // casque audio
  if (!lvl) { placeholder(g, w * 0.7, d); return; }
  // Baie radio : récepteurs et voyants
  B(g, 1.0, 1.6, 0.5, 0x3f4448, -2.5, 0, -d / 2 + 0.4);
  for (let i = 0; i < 3; i++) { B(g, 0.85, 0.3, 0.04, 0x2a2c28, -2.5, 0.25 + i * 0.45, -d / 2 + 0.66); B(g, 0.12, 0.05, 0.02, glow(i % 2 ? 0x5fe08a : 0xe8a33d), -2.7, 0.4 + i * 0.45, -d / 2 + 0.69); }
  crate(g, 2.2, 1.4, 0.7, 0.4, 0.5, C.oliveDark, 0.3);
  if (lvl >= 2) { Cyl(g, 0.015, 2.2, C.metal, -2.5, 2.7, -d / 2 + 0.3); B(g, 0.6, 0.6, 0.04, 0x8a9095, -2.5, 3.3, -d / 2 + 0.3); }
}

function rest(g: THREE.Group, lvl: number, w: number, d: number) {
  // Tapis, bâche au mur
  B(g, 3.2, 0.02, 2.3, 0x6a3a2c, -0.5, 0, 0.3);
  B(g, 2.9, 0.021, 2.0, 0x7d4a36, -0.5, 0, 0.3);
  B(g, 2.6, 1.7, 0.04, 0x5b5a44, 0.6, 0.7, -d / 2 + 0.12);
  for (let i = 0; i < 6; i++) B(g, 0.18, 0.5 + (i % 3) * 0.2, 0.01, 0x3f4436, -0.5 + i * 0.42, 0.85, -d / 2 + 0.15);
  if (!lvl) { placeholder(g, w, d); return; }
  // Canapé, table basse avec une carte et une tasse
  const sofa = new THREE.Group(); sofa.position.set(-1.5, 0, -0.7); sofa.rotation.y = 0.3; g.add(sofa);
  B(sofa, 2.0, 0.42, 0.85, 0x5a3828, 0, 0, 0);
  B(sofa, 2.0, 0.55, 0.22, 0x4e3022, 0, 0.42, -0.32);
  for (const x of [-0.95, 0.95]) B(sofa, 0.2, 0.62, 0.85, 0x4e3022, x, 0, 0);
  for (const x of [-0.45, 0.45]) B(sofa, 0.85, 0.12, 0.6, 0x6a4430, x, 0.42, 0.05);
  B(g, 1.2, 0.06, 0.7, C.wood, -0.3, 0.42, 0.7);
  for (const x of [-0.82, 0.22]) for (const z of [0.42, 0.98]) B(g, 0.06, 0.42, 0.06, C.woodDark, x, 0, z);
  B(g, 0.6, 0.01, 0.45, 0xc9b98f, -0.4, 0.48, 0.68, 0.2);
  Cyl(g, 0.045, 0.1, C.white, 0.05, 0.53, 0.8);
  B(g, 0.6, 0.6, 0.45, C.woodDark, -2.7, 0, -1.6); // table de chevet et lanterne
  Cyl(g, 0.07, 0.18, glow(0xffc874), -2.7, 0.7, -1.6);
  // Lit de camp
  const cot = new THREE.Group(); cot.position.set(2.1, 0, 0.4); cot.rotation.y = -0.5; g.add(cot);
  B(cot, 0.75, 0.05, 1.9, 0x55603a, 0, 0.4, 0);
  for (const x of [-0.36, 0.36]) B(cot, 0.04, 0.04, 1.9, C.metalDark, x, 0.38, 0);
  for (const z of [-0.8, 0.8]) for (const x of [-0.3, 0.3]) B(cot, 0.04, 0.4, 0.04, C.metalDark, x, 0, z);
  B(cot, 0.5, 0.1, 0.32, C.cloth, 0, 0.45, -0.7);
  if (lvl >= 2) { B(g, 0.9, 0.7, 0.5, 0x3f4448, 2.4, 0, -d / 2 + 0.5); Cyl(g, 0.12, 0.3, 0x2d4a5e, 2.4, 0.85, -d / 2 + 0.5); B(cot, 0.7, 0.06, 1.0, 0x6b5a40, 0, 0.47, 0.3); }
}
const BUILDERS: Record<ModuleId, (g: THREE.Group, lvl: number, w: number, d: number) => void> = { generator, workbench, medstation, storage, tailor, radio, armory, rest };

// ---------------------------------------------------------------- Hangar

function banner(): THREE.Material {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 320;
  const g = c.getContext('2d')!;
  g.fillStyle = '#2b2d2c'; g.fillRect(0, 0, 512, 320);
  g.fillStyle = '#3a3d3a';
  for (let i = 0; i < 26; i++) { const x = i * 20 + (i % 3) * 5, h = 60 + ((i * 53) % 70); g.beginPath(); g.moveTo(x - 14, 200); g.lineTo(x, 200 - h); g.lineTo(x + 14, 200); g.fill(); }
  g.fillRect(70, 70, 12, 130); g.fillRect(50, 60, 52, 22);
  g.save(); g.translate(256, 250); g.rotate(-0.06);
  g.font = '700 92px "Barlow Condensed", "Arial Narrow", sans-serif'; g.textAlign = 'center';
  g.fillStyle = '#d9d4c8'; g.fillText('BLACKSITE', 0, 20);
  g.restore();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return new THREE.MeshStandardMaterial({ map: t, roughness: 1, side: THREE.DoubleSide });
}

function hall(scene: THREE.Scene) {
  const g = new THREE.Group();
  scene.add(g);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(40, 30), flat(C.floor, { rough: 0.95 }));
  floor.rotation.x = -Math.PI / 2; floor.position.set(0, 0, -0.5); floor.receiveShadow = true; g.add(floor);
  for (let x = -12; x <= 12; x += 3) B(g, 0.03, 0.005, 17, 0x252729, x, 0, -0.3);
  for (let z = -8; z <= 8; z += 3) B(g, 26, 0.005, 0.03, 0x252729, 0, 0, z);
  // Murs : fond, côtés, cloisons entre les pièces
  B(g, 30, 5.5, 0.4, C.wall, 0, 0, -8.4);
  B(g, 30, 0.3, 0.5, C.wallDark, 0, 5.5, -8.4);
  for (let x = -14.5; x <= 14.5; x += 4.8) B(g, 0.35, 5.5, 0.25, C.wallDark, x, 0, -8.1);
  for (const x of [-14.6, 14.6]) B(g, 0.4, 5.5, 16.4, C.wall, x, 0, -0.4);
  for (const x of [-7, 0, 7]) { B(g, 0.25, 3.0, 5.4, C.wallDark, x, 0, -5.5); B(g, 0.3, 0.12, 5.4, C.trim, x, 3.0, -5.5); B(g, 0.2, 1.3, 5.2, C.wallDark, x, 0, 3.5); }
  // Tuyaux le long du mur du fond
  for (const [y, r] of [[4.6, 0.12], [4.25, 0.08]] as const) Cyl(g, r, 29, C.metalDark, 0, y, -8.05, 0, Math.PI / 2);
  // Bannière
  const ban = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 2.6), banner());
  ban.position.set(-11.6, 3.6, -8.17); ban.rotation.z = 0.05; g.add(ban);
  // Allée centrale : bandes jaunes au sol
  const tape = flat(0xc99a2a, { rough: 0.7 });
  for (const z of [-2.15, 0.25]) B(g, 28, 0.012, 0.12, tape, 0, 0, z);
  // Rambarde devant
  for (let x = -14.2; x <= 14.2; x += 1.6) B(g, 0.06, 1.05, 0.06, C.metal, x, 0, 7.4);
  B(g, 28.6, 0.06, 0.06, 0xb08a2a, 0, 1.05, 7.4);
  B(g, 28.6, 0.04, 0.04, C.metal, 0, 0.55, 7.4);
  // Quelques caisses et casiers dans l'allée
  locker(g, -14.2, -1.4, Math.PI / 2); locker(g, -14.2, -0.8, Math.PI / 2);
  crate(g, 13.6, -0.9, 0.9, 0.55, 0.6, C.olive, 0.4);
  crate(g, 6.2, -0.8, 0.7, 0.45, 0.5, C.tan, 0.2);
  return g;
}

// ---------------------------------------------------------------- Scène

interface RoomView { def: Room; group: THREE.Group; props: THREE.Group; frame: THREE.Group; glow: THREE.Mesh; hit: THREE.Mesh; light: THREE.PointLight; lvl: number }

export class BaseScene {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(30, 1, 0.5, 120);
  private rooms = new Map<ModuleId, RoomView>();
  private selected: ModuleId = 'workbench';
  private hover: ModuleId | null = null;
  private yaw = 0;
  private yawGoal = 0;
  private zoom = 1;
  private drag: { x: number; moved: boolean } | null = null;
  private thumbs = new Map<string, string>();
  private ray = new THREE.Raycaster();
  panel = 400; // largeur du panneau de droite (pixels) : la vue se décale pour centrer le hangar à sa gauche

  constructor(private canvas: HTMLCanvasElement, private tags: HTMLElement, private lines: SVGSVGElement,
    private onSelect: (id: ModuleId) => void, private visible: () => boolean) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene.background = new THREE.Color(0x0e1012);
    this.scene.fog = new THREE.Fog(0x0e1012, 34, 60);
    this.scene.add(new THREE.HemisphereLight(0xb8c4d2, 0x2a221c, 1.15));
    const moon = new THREE.DirectionalLight(0xd8e0ec, 1.1);
    moon.position.set(-8, 20, 10);
    moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    Object.assign(moon.shadow.camera, { left: -16, right: 16, top: 12, bottom: -12, near: 1, far: 50 });
    moon.shadow.bias = -0.0005;
    this.scene.add(moon);
    hall(this.scene);
    for (const def of ROOMS) this.addRoom(def);
    this.bindInput();
    this.tags.innerHTML = ROOMS.map(r => `<button class="bTag" data-m="${r.id}"><i>${MODULE_ICON[r.id]}</i><span><b>${MODULES.find(m => m.id === r.id)!.name}</b><small></small></span></button>`).join('');
    this.tags.onclick = e => { const b = (e.target as HTMLElement).closest('[data-m]') as HTMLElement | null; if (b) this.pick(b.dataset.m as ModuleId); };
    this.tags.onpointerover = e => { const b = (e.target as HTMLElement).closest('[data-m]') as HTMLElement | null; this.hover = b ? b.dataset.m as ModuleId : null; };
    this.tags.onpointerout = () => { this.hover = null; };
    requestAnimationFrame(this.loop);
  }

  private addRoom(def: Room) {
    const group = new THREE.Group();
    group.position.set(def.x, 0, def.z);
    this.scene.add(group);
    const mat = B(group, def.w - 0.3, 0.02, def.d - 0.3, C.floorMat, 0, 0, 0);
    mat.castShadow = false;
    // Cadre jaune autour de la pièce (sélection) et lueur au sol
    const frame = new THREE.Group();
    const fm = flat(0xf0b356, { emissive: 0xe8a33d });
    const t = 0.09;
    B(frame, def.w, 0.03, t, fm, 0, 0.02, -def.d / 2);
    B(frame, def.w, 0.03, t, fm, 0, 0.02, def.d / 2);
    B(frame, t, 0.03, def.d, fm, -def.w / 2, 0.02, 0);
    B(frame, t, 0.03, def.d, fm, def.w / 2, 0.02, 0);
    // Bandes de danger le long du bord avant
    for (let x = -def.w / 2 + 0.2; x < def.w / 2 - 0.2; x += 0.5) B(frame, 0.25, 0.025, 0.22, flat(0xd9a52a, { emissive: 0x6a4a10 }), x, 0.02, def.d / 2 - 0.2, 0.6);
    frame.traverse(o => { o.castShadow = false; });
    group.add(frame);
    const glowMesh = new THREE.Mesh(new THREE.PlaneGeometry(def.w, def.d), new THREE.MeshBasicMaterial({ color: 0xe8a33d, transparent: true, opacity: 0.07, depthWrite: false, blending: THREE.AdditiveBlending }));
    glowMesh.rotation.x = -Math.PI / 2; glowMesh.position.y = 0.04;
    group.add(glowMesh);
    const hit = new THREE.Mesh(new THREE.BoxGeometry(def.w, 3, def.d), new THREE.MeshBasicMaterial({ visible: false }));
    hit.position.y = 1.5; hit.userData.room = def.id;
    group.add(hit);
    const light = new THREE.PointLight(0xffd9a8, 20, 11, 1.4);
    light.position.set(0, 3.3, 0.4);
    group.add(light);
    hangLamp(group, 0, 0.3, 3.6);
    wallLamp(group, -def.w / 2 + 1.0, 2.7, -def.d / 2 + 0.15);
    const props = new THREE.Group();
    group.add(props);
    this.rooms.set(def.id, { def, group, props, frame, glow: glowMesh, hit, light, lvl: -1 });
  }

  // Niveaux des modules : reconstruit le décor des pièces qui ont changé
  setLevels(levels: Partial<Record<ModuleId, number>>, selected: ModuleId) {
    this.selected = selected;
    for (const [id, r] of this.rooms) {
      const lvl = levels[id] ?? 0, max = MODULES.find(m => m.id === id)!.levels.length;
      if (lvl !== r.lvl) {
        r.props.traverse(o => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
        r.props.clear();
        seed = 7 + ROOMS.findIndex(x => x.id === id) * 101;
        BUILDERS[id](r.props, lvl, r.def.w, r.def.d);
        r.light.intensity = lvl ? 34 : 9;
        r.lvl = lvl;
      }
      const tag = this.tags.querySelector(`[data-m=${id}]`)!;
      tag.classList.toggle('off', !lvl);
      tag.querySelector('small')!.innerHTML = `${lvl ? `Niveau ${lvl}` : 'À construire'}<em>${Array.from({ length: max }, (_, i) => `<u class="${i < lvl ? 'on' : ''}"></u>`).join('')}</em>`;
    }
  }

  private pick(id: ModuleId) {
    if (id === this.selected) return;
    this.selected = id;
    this.onSelect(id);
  }

  private bindInput() {
    const c = this.canvas;
    const roomAt = (e: PointerEvent | MouseEvent) => {
      const r = c.getBoundingClientRect();
      this.ray.setFromCamera(new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1), this.camera);
      const hit = this.ray.intersectObjects([...this.rooms.values()].map(v => v.hit))[0];
      return hit ? hit.object.userData.room as ModuleId : null;
    };
    c.onpointerdown = e => { if (e.button === 0) { this.drag = { x: e.clientX, moved: false }; c.setPointerCapture(e.pointerId); } };
    c.onpointermove = e => {
      if (this.drag) {
        const dx = e.clientX - this.drag.x;
        if (Math.abs(dx) > 3) this.drag.moved = true;
        if (this.drag.moved) { this.yawGoal = Math.max(-0.45, Math.min(0.45, this.yawGoal - dx * 0.003)); this.drag.x = e.clientX; }
        return;
      }
      this.hover = roomAt(e);
      c.style.cursor = this.hover ? 'pointer' : 'grab';
    };
    c.onpointerup = e => {
      if (this.drag && !this.drag.moved) { const id = roomAt(e); if (id) this.pick(id); }
      this.drag = null;
    };
    c.onpointerleave = () => { if (!this.drag) this.hover = null; };
    c.onwheel = e => { e.preventDefault(); this.zoom = Math.max(0.7, Math.min(1.25, this.zoom * (e.deltaY > 0 ? 1.08 : 0.93))); };
  }

  // Vignette du module pour le panneau : la pièce vue de près
  thumbnail(id: ModuleId) {
    const r = this.rooms.get(id)!;
    const key = `${id}:${r.lvl}`;
    if (this.thumbs.has(key)) return this.thumbs.get(key)!;
    const cam = new THREE.PerspectiveCamera(38, 2.4, 0.3, 60);
    cam.position.set(r.def.x + 1.2, 3.4, r.def.z + r.def.d / 2 + 3.2);
    cam.lookAt(r.def.x, 0.9, r.def.z - 0.4);
    for (const v of this.rooms.values()) { v.frame.visible = false; v.glow.visible = false; }
    const size = this.renderer.getSize(new THREE.Vector2()), pr = this.renderer.getPixelRatio();
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(432, 180, false);
    this.renderer.render(this.scene, cam);
    const url = this.canvas.toDataURL('image/jpeg', 0.86);
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(size.x, size.y, false);
    this.thumbs.set(key, url);
    return url;
  }

  private loop = (now: number) => {
    requestAnimationFrame(this.loop);
    if (!this.visible()) return;
    const c = this.canvas, rect = c.getBoundingClientRect();
    if (!rect.width) return;
    if (c.width !== Math.round(rect.width * this.renderer.getPixelRatio()) || c.height !== Math.round(rect.height * this.renderer.getPixelRatio())) this.renderer.setSize(rect.width, rect.height, false);
    // Caméra : vue plongeante, légère dérive, décalée pour laisser la place au panneau
    this.yaw += (this.yawGoal - this.yaw) * 0.08;
    const aspect = rect.width / rect.height;
    const free = Math.max(0.35, (rect.width - this.panel) / rect.width);
    const hfov = 2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * aspect);
    const dist = Math.max(24, Math.min(52, 18 / (Math.tan(hfov / 2) * free))) * this.zoom;
    const yaw = this.yaw + Math.sin(now / 9000) * 0.015, pitch = 0.86;
    const target = new THREE.Vector3(0, 0, -0.6);
    this.camera.position.set(target.x + Math.sin(yaw) * Math.cos(pitch) * dist, Math.sin(pitch) * dist, target.z + Math.cos(yaw) * Math.cos(pitch) * dist);
    this.camera.lookAt(target);
    this.camera.aspect = aspect;
    this.camera.setViewOffset(rect.width, rect.height, this.panel / 2, 0, rect.width, rect.height);
    this.camera.updateProjectionMatrix();
    // Sélection et survol
    for (const [id, r] of this.rooms) {
      const sel = id === this.selected, hov = id === this.hover;
      r.frame.visible = sel || hov;
      r.glow.visible = sel;
      (r.glow.material as THREE.MeshBasicMaterial).opacity = 0.06 + Math.sin(now / 500) * 0.02;
      r.frame.scale.setScalar(sel ? 1 : 0.985);
    }
    this.renderer.render(this.scene, this.camera);
    this.placeTags(rect);
  };

  // Étiquettes : au-dessus de chaque pièce, reliées par un trait au point visé
  private placeTags(rect: DOMRect) {
    let path = '';
    for (const [id, r] of this.rooms) {
      const p = new THREE.Vector3(r.def.x + r.def.anchor[0], r.def.anchor[1], r.def.z + r.def.anchor[2]).project(this.camera);
      const x = (p.x * 0.5 + 0.5) * rect.width, y = (-p.y * 0.5 + 0.5) * rect.height;
      const tx = x + r.def.tag[0], ty = Math.max(70, y + r.def.tag[1]);
      const el = this.tags.querySelector<HTMLElement>(`[data-m=${id}]`)!;
      el.style.transform = `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) translate(-50%, -100%)`;
      el.classList.toggle('on', id === this.selected);
      el.classList.toggle('hov', id === this.hover);
      path += `<line x1="${tx.toFixed(1)}" y1="${ty.toFixed(1)}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" class="${id === this.selected ? 'on' : ''}"/><circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" class="${id === this.selected ? 'on' : ''}"/>`;
    }
    this.lines.innerHTML = path;
  }
}
